import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, AxiosResponse, CanceledError, HttpStatusCode } from "axios";
import DocumentFormAPIRequest from "documents/_api/commands/document-form-api-request";
import DocumentQuery from "documents/_api/queries/document-query";
import UpdateDocumentAPIRequest from "documents/_api/commands/update-document-api-request";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import Session from "users/__session/session";

export default class DocumentAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithFormData(): AxiosHeaders {
    return this.headers.concat({ 'Content-Type': 'multipart/form-data' });
  }

  constructor(session: Session) {
    if (!session.authToken) throw new Error("Cannot create DocumentAPIService without session.");
    this.headers.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async getDocumentsInfo(
    query: DocumentQuery,
    abortController?: AbortController | undefined,
  ): Promise<PaginatedResponse<Document>> {

    try {
      const queryString = query.searchParams.toString();
      const response = await axios.get(
        `${AttorneyHubAPIService.apiBaseUrl}/documents?${queryString}`,
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      const documents = Document.fromJSONArray(response.data);

      return PaginatedResponse.from<Document>(
        documents,
        response.headers['x-pagination']
      );
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      throw new DocumentAPIServiceError(
        "getDocumentsInfo",
        error
      );
    }
  }

  async getDocumentInfoById(
    id: Guid, abortController: AbortController | undefined): Promise<Document | UnavailableDocument | undefined> {
    if (!id) throw new Error("Cannot get document without id.");

    try {
      const url = new URL(
        `/documents/${id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return Document.fromJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError)
        return;
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new DocumentNotFoundError(id);
      }
      throw new DocumentAPIServiceError(
        "getDocumentInfoById",
        error
      );
    }
  }

  async getDocumentsInfoByIds(
    ids: Guid[], abortController: AbortController | undefined): Promise<(Document | UnavailableDocument)[]> {
    if (ids.length === 0) throw new Error("Cannot get documents without ids.");

    try {
      const url = new URL(
        `/documents/get-documents-info-by-ids`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        ids.map(id => id.value),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return Document.fromJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new DocumentNotFoundError();
      }
      throw new DocumentAPIServiceError(
        "getDocumentsInfoByIds",
        error
      );
    }
  }

  async downloadDocument(id: Guid): Promise<AxiosResponse> {
    if (!id) throw new Error("Cannot document without id.");

    try {
      const url = new URL(
        `/documents/${id.value}/download`,
        AttorneyHubAPIService.apiBaseUrl
      );
      return await axios.get(
        url.toString(),
        {
          headers: this.headers,
          responseType: 'blob'
        }
      );
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new DocumentNotFoundError(id);
      }
      throw new DocumentAPIServiceError(
        "downloadDocument",
        error
      );
    }
  }

  async updateDocument(
    originalDocument: Document, 
    updatedDocument: Document
  ): Promise<Document | UnavailableDocument> {
    if (!originalDocument.id || !updatedDocument.id) {
      throw new Error("Cannot update document without id.");
    }

    const request = new UpdateDocumentAPIRequest(
      originalDocument,
      updatedDocument
    );

    if (request.payload.length === 0) return updatedDocument;

    try {
      const url = new URL(
        `/documents/${originalDocument.id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.patch(
        url.toString(),
        request.payload,
        {
          headers: this.headers
        }
      );
      return Document.fromJSON(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.NotFound)
          throw new DocumentNotFoundError(originalDocument.id);
        if (error.response?.status === HttpStatusCode.BadRequest)
          throw new DocumentUpdateError(
            updatedDocument,
            error.response.data
          );
      }
      throw new DocumentAPIServiceError(
        "updateDocument",
        error
      );
    }
  }

  async deleteDocument(id: Guid): Promise<void> {
    if (!id) throw new Error("Cannot delete document without id.");
    try {
      const url = new URL(
        `/documents/${id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.delete(
        url.toString(),
        {
          headers: this.headers
        }
      );
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new DocumentNotFoundError(id);
      }
      throw new DocumentAPIServiceError(
        "deleteDocument",
        error
      );
    }
  }

  async createDocument(
    file: File,
    name?: string,
    topics?: DocumentTopic[],
    templateIds?: Guid[]
  ): Promise<Document | UnavailableDocument> {
    try {
      const request = new DocumentFormAPIRequest(
        file,
        name,
        topics,
        templateIds
      );
      const url = new URL(
        `/documents`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        request.formData,
        {
          headers: this.headersWithFormData()
        }
      );
      return Document.fromJSON(response.data);
    } catch (error) {
      throw new DocumentAPIServiceError(
        "createDocument",
        error
      );
    }
  }
}

export class DocumentNotFoundError extends Error {
  constructor(id?: Guid) {
    if (id)
      super(`Document with id ${id} not found.`);
    else
      super("One or more documents not found.");
  }
}

export class DocumentUpdateError extends Error {
  updatedDocument: Document;
  errorMessage: string;

  constructor(updatedDocument: Document, errorMessage: string) {
    super("Document update failed.");
    this.updatedDocument = updatedDocument;
    this.errorMessage = errorMessage;
  }
}

export class DocumentAPIServiceError extends Error {
  method: string;
  error: unknown;

  constructor(method: string, error: unknown) {
    super(`DocumentAPIService.${method} failed: ${error ?? 'Unknown error'}`);
    this.method = method;
    this.error = error;
  }
}
